import { StyledToolbarButton, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { useBulkEditState, useGetBulkEditItemsFromSelection } from './bulk-edit.state';
import { useMutationWithCacheUpdate, useTableDataState } from '../../state/table-data.state';

import activateIcon from '@ingka/ssr-icon/paths/clipboard-checkmark';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import uniq from 'lodash/uniq';
import { useMemo } from 'react';

export const BulkEditActivateButton = () => {
   const { bulkEditSubItems, onlyLockedSubItems } = useGetBulkEditItemsFromSelection();
   const sharedSubItems = bulkEditSubItems.flatMap((s) => s.subItems);

   const allSelectionActive = useMemo(() => {
      if (sharedSubItems.length === 0) return true;
      const actives = uniq(sharedSubItems.map((s) => s.active));
      return actives.length === 1 && actives[0];
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [bulkEditSubItems]);

   const tooltipText = allSelectionActive
      ? 'You need to select unlocked inactive materials to activate'
      : 'Activate all inactive materials in selection';

   const mutate = useMutationWithCacheUpdate('Material(s) successfully activated');

   const onClick = () => {
      const updated = sharedSubItems
         .filter((s) => !s.locked)
         .map((subItem) => set(cloneDeep(subItem), 'active', true));

      const updatedIds = new Set(updated.map((u) => `${u.parentId}:${u.id}`));
      const previousBulkValues = new Map(sharedSubItems.map((s) => [`${s.parentId}:${s.id}`, s.active]));
      const previousTableValues = new Map(sharedSubItems.map((s) => [`${s.parentId}:${s.id}`, s.active]));

      useBulkEditState.setState((state) => ({
         tableData: state.tableData.map((item) => {
            const updatedSubItems = item.subItems.map((s) =>
               updatedIds.has(`${s.parentId}:${s.id}`) ? { ...s, active: true } : s
            );
            return {
               ...item,
               subItems: updatedSubItems,
               inactive: updatedSubItems.filter((s) => !s.active).length,
            };
         }),
      }));

      useTableDataState.setState((state) => ({
         data: state.data.map((item) => ({
            ...item,
            subItems: item.subItems?.map((s) =>
               updatedIds.has(`${s.parentId}:${s.id}`) ? { ...s, active: true } : s
            ),
         })),
      }));

      mutate(updated, {
         onError: () => {
            useBulkEditState.setState((state) => ({
               tableData: state.tableData.map((item) => {
                  const rolledBackSubItems = item.subItems.map((s) =>
                     previousBulkValues.has(`${s.parentId}:${s.id}`)
                        ? { ...s, active: previousBulkValues.get(`${s.parentId}:${s.id}`)! }
                        : s
                  );
                  return {
                     ...item,
                     subItems: rolledBackSubItems,
                     inactive: rolledBackSubItems.filter((s) => !s.active).length,
                  };
               }),
            }));
            useTableDataState.setState((state) => ({
               data: state.data.map((item) => ({
                  ...item,
                  subItems: item.subItems?.map((s) =>
                     previousTableValues.has(`${s.parentId}:${s.id}`)
                        ? { ...s, active: previousTableValues.get(`${s.parentId}:${s.id}`)! }
                        : s
                  ),
               })),
            }));
         },
      });
   };

   return (
      <StyledTooltip title={tooltipText}>
         <div>
            <StyledToolbarButton
               ssrIcon={activateIcon}
               iconOnly={true}
               small={true}
               onClick={onClick}
               disabled={allSelectionActive || onlyLockedSubItems}
            />
         </div>
      </StyledTooltip>
   );
};